import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom'; // <-- Imported Link
import PageBackground from '../components/PageBackground';
import JaapaSettings from '../components/Jaapa/JaapaSettings';
import JaapaCounter from '../components/Jaapa/JaapaCounter';
import ChantingArea from '../components/Jaapa/ChantingArea';
import VoiceSelector from '../components/Jaapa/VoiceSelector';
import useSpeechSynthesis from '../hooks/useSpeechSynthesis';
import apiService from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import './BhaktiJaapaPage.css';

const BhaktiJaapaPage = () => {
  const { t, language } = useLanguage();
  const [selectedName, setSelectedName] = useState('Om Namaha Shivaya');
  const [sessionCount, setSessionCount] = useState(0);
  const [malaCount, setMalaCount] = useState(0);
  const currentBead = sessionCount % 108;

  // --- NEW STATE FOR TODAY'S ANALYTICS ---
  const [todaysTotal, setTodaysTotal] = useState(0);
  const [todaysMalas, setTodaysMalas] = useState(0);

  const audioRef = useRef(null);
  const [isSoundOn, setIsSoundOn] = useState(() => {
    try {
      return localStorage.getItem('jaapaBackgroundSound') === 'on';
    } catch (error) {
      return false;
    }
  });
  const [hasInteracted, setHasInteracted] = useState(false);
  
  const { voices, selectedVoice, setSelectedVoice, speak } = useSpeechSynthesis();
  const unsyncedCount = useRef(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    document.body.classList.add('bhakti-jaapa-theme');
    return () => {
      document.body.classList.remove('bhakti-jaapa-theme');
    };
  }, []);

  // Fetch today's analytics when the component mounts
  useEffect(() => {
    const fetchTodaysAnalytics = async () => {
      try {
        const response = await apiService.getJaapaAnalytics('today');
        if (response.success) {
          setTodaysTotal(response.data.grandTotal || 0);
          setTodaysMalas(response.data.grandTotalMalas || 0);
        }
      } catch (error) {
        console.error("Failed to fetch today's analytics:", error);
      }
    };
    fetchTodaysAnalytics();
  }, []);

  const handleChant = () => {
    // --- START AUDIO ON FIRST CHANT ---
    if (!hasInteracted) {
      if (audioRef.current && isSoundOn) {
        audioRef.current.play().catch(error => console.error("Audio autoplay failed:", error));
      }
      setHasInteracted(true);
    }
    // ---------------------------------
    
    const newCount = sessionCount + 1;
    setSessionCount(newCount);
    speak(selectedName);

    unsyncedCount.current += 1; // Increment unsynced count
    setTodaysTotal(prevTotal => prevTotal + 1); // Instantly update today's total on UI

    // --- UPDATE SAVE LOGIC ---
    if (unsyncedCount.current >= 10) {
      apiService.logJaapaCount(selectedName, unsyncedCount.current);
      unsyncedCount.current = 0; // Reset after syncing
    }

    // Check if a mala has been completed
    if (newCount > 0 && newCount % 108 === 0) {
      setMalaCount(prev => prev + 1);
      setTodaysMalas(prev => prev + 1);
      // Sync any remaining chants plus log this as a mala completion
      if (unsyncedCount.current > 0) {
        apiService.logJaapaCount(selectedName, unsyncedCount.current);
        unsyncedCount.current = 0;
      }
      apiService.logJaapaCount(selectedName, 0, true); // Log mala completion marker
    }
  };

  const toggleBackgroundSound = () => {
    setIsSoundOn((previousValue) => {
      const newIsSoundOn = !previousValue;
      if (newIsSoundOn) {
        audioRef.current?.play().catch(e => console.error(e));
      } else {
        audioRef.current?.pause();
      }
      return newIsSoundOn;
    });
  };

  useEffect(() => {
    try {
      localStorage.setItem('jaapaBackgroundSound', isSoundOn ? 'on' : 'off');
    } catch (error) {
      // no-op: localStorage may be unavailable in restricted environments
    }
  }, [isSoundOn]);

  // --- RESET LOGIC ---
  useEffect(() => {
    setSessionCount(0);
    setMalaCount(0);
  }, [selectedName]);

  // Save any remaining count when the user leaves the page or changes the name
  useEffect(() => {
    // Cleanup function now uses the new log endpoint
    return () => {
      if (unsyncedCount.current > 0) {
        apiService.logJaapaCount(selectedName, unsyncedCount.current);
        unsyncedCount.current = 0;
      }
      // Stop the music when leaving the page
      audioRef.current?.pause();
    };
  }, [selectedName]);

  const imageUrl = '/images/bg.avif';
  const numberLocale = language === 'hi' ? 'hi-IN' : language === 'or' ? 'or-IN' : 'en-IN';
  const publicBase = process.env.PUBLIC_URL || '';
  const jaapaImageBase = `${publicBase}/images/jaapa`;
  const sideImageVersion = '20260601';

  return (
    <div className="bhakti-jaapa-page">
      <audio ref={audioRef} src="/audio/ambient-flute.mp3" loop muted={!isSoundOn} />
      <PageBackground imageUrl={imageUrl} />
      
      <div className="jaapa-page-header">
        <h1>{t('bhaktiJaapaPage.header.title')}</h1>
        <p>{t('bhaktiJaapaPage.header.subtitle')}</p>
      </div>

      <div className="jaapa-main-content">
        <div className="container jaapa-interface-container">
          <div className="jaapa-layout">
            <div className="jaapa-side-column jaapa-side-column-left" aria-hidden="true">
              <div className="jaapa-side-image jaapa-side-image-left">
                <img src={`${jaapaImageBase}/jagannatha-temple-puri.webp?v=${sideImageVersion}`} alt="" loading="lazy" />
              </div>
              <div className="jaapa-side-image jaapa-side-image-left-secondary">
                <img src={`${jaapaImageBase}/krishna.webp?v=${sideImageVersion}`} alt="" loading="lazy" />
              </div>
              <div className="jaapa-side-image jaapa-side-image-left-tertiary">
                <img src={`${jaapaImageBase}/shiva2.webp?v=${sideImageVersion}`} alt="" loading="lazy" />
              </div>
            </div>

            <div className="jaapa-interface-card">
            <JaapaSettings selectedName={selectedName} onNameChange={setSelectedName} />
            
            <ChantingArea
              onChant={handleChant}
              divineName={selectedName}
              currentBead={currentBead}
              totalBeads={108}
            />
            
            <div className="counters-wrapper">
              <JaapaCounter label={t('bhaktiJaapaPage.counters.sessionJaapa')} count={sessionCount} />
              <JaapaCounter label={t('bhaktiJaapaPage.counters.malasCompleted')} count={malaCount} isMalaCounter={true} />
            </div>

            {/* --- TODAY'S ANALYTICS SECTION --- */}
            <div className="todays-analytics">
              <p>{t('bhaktiJaapaPage.analytics.todaysTotalJaapas')}: <strong>{todaysTotal.toLocaleString(numberLocale)}</strong></p>
              <p>{t('bhaktiJaapaPage.analytics.todaysMalasCompleted')}: <strong>{todaysMalas.toLocaleString(numberLocale)}</strong></p>
            </div>
            {/* ------------------------------------- */}

            <VoiceSelector voices={voices} selectedVoice={selectedVoice} onVoiceChange={setSelectedVoice} />

            <div className="background-sound-control">
              <span className="background-sound-label">{t('bhaktiJaapaPage.soundToggle.backgroundFlute')}</span>
              <button
                type="button"
                onClick={toggleBackgroundSound}
                className="background-sound-btn"
                aria-label={t('bhaktiJaapaPage.soundToggle.toggleBackgroundSound')}
                title={t('bhaktiJaapaPage.soundToggle.toggleBackgroundSound')}
              >
                {isSoundOn ? t('bhaktiJaapaPage.soundToggle.mute') : t('bhaktiJaapaPage.soundToggle.unmute')}
              </button>
            </div>
            
            {/* --- SHORTCUT BUTTON --- */}
            <div className="analytics-shortcut">
              <Link to="/dashboard/jaapa-analytics" className="shortcut-btn">
                <i className="fas fa-chart-line"></i> {t('bhaktiJaapaPage.analytics.viewMyAnalytics')}
              </Link>
            </div>
            {/* ----------------------- */}
            </div>

            <div className="jaapa-side-column jaapa-side-column-right" aria-hidden="true">
              <div className="jaapa-side-image jaapa-side-image-right">
                <img src={`${jaapaImageBase}/jay-jagannath-ratha.webp?v=${sideImageVersion}`} alt="" loading="lazy" />
              </div>
              <div className="jaapa-side-image jaapa-side-image-right-secondary">
                <img src={`${jaapaImageBase}/ma-kali.webp?v=${sideImageVersion}`} alt="" loading="lazy" />
              </div>
              <div className="jaapa-side-image jaapa-side-image-right-tertiary">
                <img src={`${jaapaImageBase}/isckon.webp?v=${sideImageVersion}`} alt="" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BhaktiJaapaPage;
